import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Card,
  CardContent,
  TableRow,
  TableContainer,
  Table,
  TableBody,
  Button,
  TableHead,
  Skeleton,
  Typography,
} from "@mui/material";

import { ActionButton } from "components/ActionButton";
import { DeleteLine } from "components/EditPage/DeleteLine";
import { LineItemText } from "components/formFields/LineItemText";
import { LineTableCell } from "components/formFields/LineTableCell";
import { LineColumnHeading } from "components/formFields/line/LineColumnHeading";
import { VSpace } from "components/utility/VSpace";

import { retrieveSettings } from "services/sosInventoryService/settings";
import {
  getSupportAccessActivations,
  deleteSupportAccessActivation,
} from "services/sosInventoryService/sosApi";
import { getRefreshedBearerToken } from "services/utility/authentication";
import { isoToLocalDateTime } from "services/utility/dates";
import { formatDateTimeToDateTimeString } from "services/utility/formatting";

import { ActivateDialog } from "views/SupportConsole/SupportAccess/ActivateDialog";

import { openAlert } from "globalState/alertSlice";
import {
  loadingIndicatorOn,
  loadingIndicatorOff,
} from "globalState/loadingSlice";
import { setCompany } from "globalState/userCompanySettingsSlice";

export function SupportAccess() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [activations, setActivations] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const companies = useSelector((state) => state.userCompanySettings.companies);
  const isListLoading = useSelector(({ loading }) => loading.list);

  useEffect(() => {
    async function _getAccessActivations() {
      setIsLoading(true);
      const response = await getSupportAccessActivations();
      const mappedData = response.map((entry) => {
        entry.activationTime = isoToLocalDateTime(entry.activationTime);
        return entry;
      });
      setActivations(mappedData);
      setIsLoading(false);
    }
    _getAccessActivations();
  }, [dispatch]);

  async function handleDelete(id) {
    dispatch(loadingIndicatorOn());
    const { success } = await deleteSupportAccessActivation(id);
    if (success) {
      const message = "Successfully terminated support access activation";
      dispatch(openAlert({ type: "success", message }));
      setActivations((activations) => activations.filter((a) => a.id !== id));
    } else {
      const message = "Error terminating support access activation";
      dispatch(openAlert({ type: "error", message }));
    }
    dispatch(loadingIndicatorOff());
  }

  async function switchToActivatedAccount(companyId) {
    dispatch(loadingIndicatorOn());
    const success = await getRefreshedBearerToken(companyId);
    if (success) {
      const activatedCompany = companies.find(({ id }) => id === companyId);
      if (Boolean(activatedCompany)) {
        dispatch(setCompany(activatedCompany));
        await retrieveSettings();
        dispatch(loadingIndicatorOff());
        navigate("/");
      } else {
        dispatch(
          openAlert({ type: "error", message: "Error switching accounts" })
        );
      }
    } else {
      dispatch(
        openAlert({ type: "error", message: "Error switching accounts" })
      );
      dispatch(loadingIndicatorOff());
    }
  }

  return (
    <>
      <Card variant="outlined" sx={{ maxWidth: 750 }}>
        <CardContent>
          <Typography sx={{ mb: 1 }} variant="h4">
            Support access
          </Typography>
          {isLoading ? (
            <Skeleton
              sx={{ width: "100%" }}
              animation="wave"
              data-testing="skeleton"
            />
          ) : activations?.length ? (
            <TableContainer
              sx={{ overflowX: "initial" }}
              data-testing="lineItems"
            >
              <Table size="small" padding="none" stickyHeader>
                <TableHead>
                  <TableRow>
                    <LineColumnHeading sx={{ displayPrint: "none" }} />
                    <LineColumnHeading labelCode="Account" />
                    <LineColumnHeading labelCode="DateActivated" />
                    <LineColumnHeading labelCode="Profile" />
                    <LineColumnHeading sx={{ displayPrint: "none" }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activations?.map(
                    (
                      { companyId, id, activationTime, privilegeProfile },
                      i
                    ) => {
                      return (
                        <TableRow key={i}>
                          <DeleteLine onClick={() => handleDelete(id)} />
                          <LineTableCell sx={{ width: "30.0rem" }}>
                            <LineItemText value={companyId} />
                          </LineTableCell>
                          <LineTableCell sx={{ width: "30.0rem" }}>
                            <LineItemText
                              value={formatDateTimeToDateTimeString(
                                activationTime
                              )}
                            />
                          </LineTableCell>
                          <LineTableCell sx={{ width: "30.0rem" }}>
                            <LineItemText value={privilegeProfile} />
                          </LineTableCell>
                          <LineTableCell sx={{ displayPrint: "none" }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <Button
                                size="small"
                                disabled={isListLoading}
                                onClick={() =>
                                  switchToActivatedAccount(companyId)
                                }
                                sx={{ lineHeight: "normal", m: 0.5 }}
                              >
                                Switch to
                              </Button>
                            </Box>
                          </LineTableCell>
                        </TableRow>
                      );
                    }
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>
              Support access has not been activated for any accounts.
            </Typography>
          )}
          <VSpace space={2} />
          <ActionButton onClick={() => setDialogOpen(true)}>
            New support access session
          </ActionButton>
        </CardContent>
      </Card>
      <ActivateDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
}
